import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useTimezone, formatDateTime, formatDate } from '@/lib/timezone';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import {
  Mail, Send, Zap, Clock, CheckCircle2, Eye, AlertTriangle,
  Play, BarChart3, Loader2, ChevronRight, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export default function CommunicationsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [automationLogs, setAutomationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [runningChase, setRunningChase] = useState(false);
  const { isAdmin } = useAuth();
  const tz = useTimezone();

  const loadLogs = async () => {
    try {
      const params = {};
      if (typeFilter !== 'all') params.template_type = typeFilter;
      const { data } = await api.get('/email-logs', { params });
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/chase/stats');
      setStats(data);
    } catch (err) { console.error(err); }
  };

  const loadAutomationLogs = async () => {
    try {
      const { data } = await api.get('/automation/logs');
      setAutomationLogs(data.items || []);
    } catch { /* admin only */ }
  };

  const load = async () => {
    setLoading(true);
    await Promise.all([loadLogs(), loadStats(), loadAutomationLogs()]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [typeFilter]);

  const handleRunScan = async () => {
    setRunningChase(true);
    try {
      const { data } = await api.post('/chase/run-scan');
      toast.success(`Chase scan complete: ${data.chases_sent} emails sent`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chase scan failed');
    } finally { setRunningChase(false); }
  };

  const statusIcon = {
    sent: <Send className="h-3 w-3" />,
    delivered: <CheckCircle2 className="h-3 w-3" />,
    opened: <Eye className="h-3 w-3" />,
    failed: <AlertTriangle className="h-3 w-3 text-destructive" />,
    draft: <Clock className="h-3 w-3" />
  };

  const severityBadge = {
    high: 'destructive',
    medium: 'secondary',
    low: 'outline'
  };

  if (loading) {
    return <div className="space-y-6"><Skeleton className="h-10 w-48" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div><Skeleton className="h-96 rounded-xl" /></div>;
  }

  return (
    <div data-testid="communications-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Communications</h1>
          <p className="text-sm text-muted-foreground">Automated vendor chasing & email history</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="mr-2 h-3 w-3" /> Refresh
          </Button>
          {isAdmin && (
            <Button data-testid="communications-run-scan-button" onClick={handleRunScan} disabled={runningChase}>
              {runningChase ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
              {runningChase ? 'Running scan...' : 'Run Chase Scan'}
            </Button>
          )}
        </div>
      </div>

      {/* Stats KPIs */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Chases', value: stats.total_chases_sent, icon: Mail, sub: `${stats.automated_chases} automated` },
            { label: 'Vendors Needing Chase', value: stats.vendors_needing_chase, icon: AlertTriangle, sub: 'Non-compliant', color: 'text-destructive' },
            { label: 'Response Rate', value: `${stats.response_rate}%`, icon: Eye, sub: 'Emails opened' },
            { label: 'Last 30 Days', value: Object.values(stats.by_template_type || {}).reduce((a,b) => a+b, 0), icon: BarChart3, sub: `${Object.keys(stats.by_template_type || {}).length} types` },
          ].map((kpi, i) => (
            <Card key={i} className="rounded-xl border shadow-[var(--shadow-soft)]">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{kpi.label}</p>
                    <p className="text-2xl font-semibold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{kpi.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                  </div>
                  <div className={`p-2 rounded-lg bg-muted ${kpi.color || 'text-primary'}`}>
                    <kpi.icon className="h-4 w-4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="logs">
        <TabsList>
          <TabsTrigger value="logs"><Mail className="mr-2 h-4 w-4" /> Email Logs ({total})</TabsTrigger>
          {isAdmin && <TabsTrigger value="automation"><Zap className="mr-2 h-4 w-4" /> Automation Runs</TabsTrigger>}
        </TabsList>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[180px]"><SelectValue placeholder="Template type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="expired">Expired Notice</SelectItem>
                    <SelectItem value="missing">Missing Request</SelectItem>
                    <SelectItem value="rejected">Invalid Notice</SelectItem>
                    <SelectItem value="expiring_soon">Expiration Warning</SelectItem>
                    <SelectItem value="follow_up">Follow-Up</SelectItem>
                    <SelectItem value="chase">Chase (Legacy)</SelectItem>
                    <SelectItem value="reminder">Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Thread List */}
            <Card data-testid="communications-thread-list" className="lg:col-span-5 rounded-xl border shadow-[var(--shadow-soft)]">
              <CardContent className="p-0">
                <ScrollArea className="h-[400px] sm:h-[500px]">
                  <div className="space-y-0 min-w-0">
                    {logs.map((log, i) => (
                      <div key={i}
                        className={`p-3 sm:p-4 cursor-pointer hover:bg-muted/50 transition-colors duration-150 border-b border-border ${selectedLog === i ? 'bg-muted' : ''}`}
                        onClick={() => setSelectedLog(i)}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-sm font-medium truncate min-w-0">{log.vendor_name}</span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {log.automation_source === 'cron' && <Zap className="h-3 w-3 text-primary" />}
                            <Badge variant="outline" className="text-[10px] sm:text-xs flex items-center gap-1">
                              {statusIcon[log.status]}
                              {log.status}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm truncate">{log.subject}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant={severityBadge[log.severity] || 'outline'} className="text-[10px] sm:text-xs">{log.template_type}</Badge>
                          <span className="text-[10px] sm:text-xs text-muted-foreground">{log.created_at ? formatDateTime(log.created_at, tz) : ''}</span>
                          {log.chase_sequence > 1 && <span className="text-[10px] sm:text-xs text-muted-foreground">#{log.chase_sequence}</span>}
                        </div>
                      </div>
                    ))}
                    {logs.length === 0 && (
                      <div className="py-16 text-center">
                        <Mail className="mx-auto h-10 w-10 text-muted-foreground/40 mb-4" />
                        <p className="text-muted-foreground text-sm">No emails found</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Thread Detail */}
            <Card data-testid="communications-thread-detail" className="lg:col-span-7 rounded-xl border shadow-[var(--shadow-soft)]">
              <CardContent className="p-6">
                {selectedLog !== null && logs[selectedLog] ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{logs[selectedLog].subject}</h3>
                        <p className="text-sm text-muted-foreground">To: {logs[selectedLog].recipient_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {logs[selectedLog].automation_source === 'cron' && <Badge className="text-xs"><Zap className="mr-1 h-3 w-3" /> Automated</Badge>}
                        <Badge variant={severityBadge[logs[selectedLog].severity] || 'outline'} className="text-xs capitalize">{logs[selectedLog].template_type}</Badge>
                      </div>
                    </div>
                    <Separator />
                    <ScrollArea className="h-[300px]">
                      <div className="bg-muted/30 rounded-lg p-4">
                        <p className="text-sm whitespace-pre-line">{logs[selectedLog].body}</p>
                      </div>
                    </ScrollArea>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span>Sent: {logs[selectedLog].created_at ? formatDateTime(logs[selectedLog].created_at, tz) : '-'}</span>
                      <span>Chase #{logs[selectedLog].chase_sequence}</span>
                      <Badge variant="outline" className="text-xs">{logs[selectedLog].status}</Badge>
                    </div>
                  </div>
                ) : (
                  <div className="h-[400px] flex items-center justify-center">
                    <div className="text-center">
                      <Mail className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
                      <p className="text-muted-foreground">Select a message to view details</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Automation Tab */}
        {isAdmin && (
          <TabsContent value="automation" className="space-y-4">
            <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Chase Scan History</CardTitle>
                <CardDescription>Automated and manual chase scan runs</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {automationLogs.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendors Scanned</TableHead>
                        <TableHead>Chases Sent</TableHead>
                        <TableHead>Skipped</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {automationLogs.map((log, i) => (
                        <TableRow key={i} className="hover:bg-muted/50 transition-colors duration-150">
                          <TableCell className="text-sm">{log.created_at ? formatDateTime(log.created_at, tz) : '-'}</TableCell>
                          <TableCell className="text-sm tabular-nums">{log.vendors_scanned}</TableCell>
                          <TableCell>
                            <Badge variant="default" className="text-xs tabular-nums">{log.chases_sent}</Badge>
                          </TableCell>
                          <TableCell className="text-sm tabular-nums text-muted-foreground">{log.chases_skipped}</TableCell>
                          <TableCell>
                            <Badge variant={log.status === 'completed' ? 'outline' : 'destructive'} className="text-xs">{log.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-12 text-center">
                    <Zap className="mx-auto h-10 w-10 text-muted-foreground/40 mb-4" />
                    <p className="text-muted-foreground text-sm">No automation runs yet</p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={handleRunScan} disabled={runningChase}>
                      Run First Scan
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chase breakdown by type */}
            {stats?.by_template_type && Object.keys(stats.by_template_type).length > 0 && (
              <Card className="rounded-xl border shadow-[var(--shadow-soft)]">
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Chase Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.by_template_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium capitalize">{type.replace('_', ' ')}</span>
                        </div>
                        <Badge variant="secondary" className="tabular-nums">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
